/**
 * Drawer.tsx
 *
 * App-wide right-side slide-in drawer. This is the standard container the
 * redesign uses in place of center modals for any form, detail view, or
 * multi-field panel. (Short "are you sure?" confirmations keep using the
 * centered ConfirmDialog instead.)
 *
 * Features:
 *   - Slides in from the right; dimmed scrim behind (click scrim to close).
 *   - Header with title (+ optional subtitle) and an X close button.
 *   - Scrollable body — only the body scrolls, header & footer stay put.
 *   - Optional sticky footer for action buttons (Cancel / Save, etc.).
 *   - Esc closes. Focus is trapped inside while open and restored on close.
 *   - Full-width on mobile (see Drawer.css).
 *   - Rendered through a portal on document.body so it always sits above
 *     page content with the correct stacking (no z-index fights).
 *
 * Styling comes entirely from --p-* design tokens (see pulse-theme.css)
 * and Drawer.css — no hardcoded colours.
 *
 * Usage:
 *   <Drawer
 *     open={open}
 *     onClose={() => setOpen(false)}
 *     title="Create Deal"
 *     subtitle="New opportunity"
 *     footer={
 *       <>
 *         <button className="p-drawer-btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
 *         <button className="p-btn" onClick={save}>Save deal</button>
 *       </>
 *     }
 *   >
 *     ... form fields ...
 *   </Drawer>
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { applyPrintTitle, restorePrintTitle } from '../../lib/printDocumentTitle';
import './Drawer.css';

export interface DrawerProps {
  /** Whether the drawer is open. */
  open: boolean;
  /** Called when the user requests close (X, scrim click, or Esc). */
  onClose: () => void;
  /** Heading shown in the header. */
  title?: React.ReactNode;
  /** Optional smaller line under the title. */
  subtitle?: React.ReactNode;
  /** Body content (the scrollable region). */
  children?: React.ReactNode;
  /** Optional sticky footer content — usually the action buttons. */
  footer?: React.ReactNode;
  /** Extra class on the scrollable body — e.g. to drop padding for a custom
   *  full-bleed layout (two-pane). Appended after the default `p-drawer-body`. */
  bodyClassName?: string;
  /** Panel width in px (desktop). Defaults to 460. Ignored on mobile (full-width). */
  width?: number;
  /**
   * Named width variant — overrides `width` with a responsive CSS width (see
   * Drawer.css). Reusable across modules: any drawer can opt into one by passing it.
   *
   *   • `'record'`      — the half-page record drawer (~52vw, clamped 560–880px).
   *     Still used by the CREATE drawers, which stay one column: they have no record
   *     id yet, so they have no activity and no tasks to put in a second one.
   *   • `'record-wide'` — the two-column RECORD VIEW (~70vw, clamped 900–1120px).
   *     Replaces the deal's hard-coded `width={1080}`; at a 1536px window it computes
   *     to ~1075px, so the deal is within 5px of where it was, and the other three
   *     grow into it instead of each picking their own number.
   */
  size?: 'record' | 'record-wide';
  /** Click the dimmed background to close. Default: true. */
  closeOnScrimClick?: boolean;
  /** Accessible label when there is no visible title. */
  ariaLabel?: string;
  /**
   * Opt this drawer into printing. Ctrl+P while it is open prints THE PANEL as a
   * document — title, subtitle and body — instead of the page behind it.
   *
   * DEFAULTS TO FALSE ON PURPOSE. DO NOT FLIP IT TO MAKE DRAWERS "CONSISTENT".
   * Roughly 70 of this app's ~79 drawers are create/edit/confirm FORMS — "New
   * Deal", "Add Vendor", "Reject expense", "Start Onboarding". A half-filled form
   * is not a document, and printing one is not a thing anybody wants; those
   * drawers printing nothing on Ctrl+P is the CORRECT behaviour, not an
   * oversight to be tidied up later. Only the ~9 RECORD/DETAIL surfaces opt in,
   * because only they represent something a reader would put on paper.
   *
   * The inconsistency is the point: it tracks a real distinction between a form
   * and a record. Flipping the default would make every abandoned create form
   * printable and would not fix anything.
   *
   * WHAT PRINTS: `.p-drawer-title`, `.p-drawer-subtitle`, `.p-drawer-body`.
   * WHAT DOESN'T: the scrim, the close button, and any control in the footer.
   * Mark surface-specific chrome inside the body with `.no-print` (the same
   * convention OrgChartPage uses) — the print rule honours it.
   *
   * Note the footer is NOT hidden wholesale, only its controls: TaskDetailModal
   * puts "Created … · Updated …" there, which belongs on the printout. Checked
   * against all nine footers before this shipped, not assumed from the prop docs.
   */
  printable?: boolean;
  /**
   * What the PRINTED page and the saved PDF are called, while this drawer prints.
   *
   * Only meaningful with `printable`. Omit it for the CRM record drawers: they render a
   * <RecordSheet>, and the title is read from the sheet's own <h1> so the tab and the
   * paper cannot disagree. Pass it for every other printable surface, whose document has
   * no such heading — an invoice its NUMBER, a quote its number, a task its title.
   *
   * Omitted AND no sheet present → the tab is left alone rather than renamed to something
   * invented. See lib/printDocumentTitle.ts.
   */
  printTitle?: string | null;
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export default function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  bodyClassName,
  width = 460,
  size,
  closeOnScrimClick = true,
  ariaLabel,
  printable = false,
  printTitle,
}: DrawerProps) {
  // `render` keeps the node mounted through the slide-out animation;
  // `shown` flips the CSS that drives the actual slide + fade.
  const [render, setRender] = useState(open);
  const [shown, setShown] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);

  // Mount on open, animate in; animate out then unmount on close.
  useEffect(() => {
    if (open) {
      lastFocused.current = document.activeElement as HTMLElement | null;
      setRender(true);
      const raf = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(raf);
    }
    setShown(false);
    const t = setTimeout(() => setRender(false), 360); // matches --p-dur-slow slide-out
    return () => clearTimeout(t);
  }, [open]);

  /**
   * Print support (opt-in via `printable`).
   *
   * Ctrl+P runs no application code, so there is no click handler to hang this
   * on — `beforeprint` is the only hook. It fires for the browser's own print
   * command AND for a programmatic window.print(), which is what makes the guard
   * below load-bearing rather than defensive.
   *
   * THE GUARD, AND WHY IT POINTS THIS WAY. The invoice and quote detail drawers
   * carry a "Download PDF" button that sets `printing-invoice` and then calls
   * window.print(). That fires beforeprint while this drawer is open — so
   * without the check, BOTH classes would be set and two document rules would be
   * live at once. The invoice rule happens to win on specificity, but relying on
   * that would be a silent dependency on arithmetic nobody reading either rule
   * can see. Refusing to add our class when a document print already owns the
   * job is explicit and survives a selector edit.
   *
   * IT ALSO NAMES THE DOCUMENT. Chrome prints `document.title` in the page header
   * and offers it as the PDF filename, so a printed record came out headed "React
   * App". `applyPrintTitle` swaps in the record's own heading and
   * `restorePrintTitle` puts the tab's title back — hung on THESE listeners rather
   * than a second pair, and restored in the cleanup for the same reason the class
   * is: nothing this print does may outlive the drawer. It renames only when the
   * drawer contains a RecordSheet; every other printable drawer is untouched. See
   * lib/printDocumentTitle.ts.
   */
  useEffect(() => {
    if (!printable || !render) return;
    // Classes belonging to a document path that replaces the page wholesale.
    const DOCUMENT_PRINTS = ['printing-invoice', 'printing-orgchart'];
    const before = () => {
      if (DOCUMENT_PRINTS.some(c => document.body.classList.contains(c))) return;
      /* THE TOPMOST PRINTABLE DRAWER OWNS THE PRINT — the same "something more specific
         has the stronger claim" rule DashboardLayout already applies to pages, one level
         down. It became reachable when a task opened on top of a CRM record: two printable
         drawers, two beforeprint handlers, and `applyPrintTitle` refusing (correctly) to
         overwrite the first swap — so the paper said the task and the tab said the deal.
         Whichever panel is LAST in the DOM is the one on screen, because each Drawer
         portals to document.body in mount order.
         Written so it can only ever bail EARLIER than before: it acts only when this panel
         is in the list AND something printable sits above it, so the one-drawer case —
         every print this app did until now — takes the identical path it always did. */
      const panels = Array.from(document.querySelectorAll('.p-drawer-panel[data-printable]'));
      const self = panelRef.current;
      if (self && panels.includes(self) && panels[panels.length - 1] !== self) return;
      document.body.classList.add('printing-drawer');
      applyPrintTitle(printTitle);
    };
    const after = () => {
      document.body.classList.remove('printing-drawer');
      restorePrintTitle();
    };
    window.addEventListener('beforeprint', before);
    window.addEventListener('afterprint', after);
    return () => {
      window.removeEventListener('beforeprint', before);
      window.removeEventListener('afterprint', after);
      // Never let the class outlive the drawer: a leaked `printing-drawer` would
      // blank every later print in the session, which is the original bug. Same
      // reasoning for the title — a leaked rename would sit in the tab all session.
      document.body.classList.remove('printing-drawer');
      restorePrintTitle();
    };
  }, [printable, render, printTitle]);

  // Lock background scroll while open.
  useEffect(() => {
    if (!render) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [render]);

  // Move focus into the panel when it opens; restore it when it closes.
  useEffect(() => {
    if (shown && panelRef.current) {
      const first = panelRef.current.querySelector<HTMLElement>(FOCUSABLE);
      (first ?? panelRef.current).focus();
    }
    if (!open && lastFocused.current) {
      lastFocused.current.focus?.();
    }
  }, [shown, open]);

  // Esc to close + Tab focus trap.
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        /* ESCAPE CLOSES THE TOPMOST THING, NOT EVERYTHING.
           A gear popover / floating menu inside this drawer renders through a PORTAL to
           document.body, so it is not a DOM descendant of the panel — but it IS a React
           child of it, and React propagates events along the REACT tree, not the DOM one.
           So Escape pressed inside an open popover arrived here as well, and the drawer
           closed out from under the popover the user was actually dismissing.
           The popover owns its own Escape (AnchoredPopover / FloatingMenu each listen on
           document). This handler therefore acts only when the key was pressed inside the
           panel itself; a portal'd overlay's target is not contained by it, so we leave
           it alone and let the overlay close. */
        if (panelRef.current && !panelRef.current.contains(e.target as Node)) return;
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const nodes = Array.from(
        panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((el) => el.offsetParent !== null);
      if (nodes.length === 0) {
        e.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const active = document.activeElement as HTMLElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose]
  );

  if (!render) return null;

  return createPortal(
    <>
      <div
        className="p-drawer-scrim"
        data-shown={shown}
        onClick={closeOnScrimClick ? onClose : undefined}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        className={`p-drawer-panel${size ? ` p-drawer-panel--${size}` : ''}`}
        data-shown={shown}
        /* "A printable drawer is open", stated in the DOM so anyone can read it.
           The app shell names a printed PAGE after its <h1>, and must not do that while a
           drawer owns the print — an open deal record has to print as the deal, not as
           "Deal Pipeline". It cannot check `body.printing-drawer` for that: the drawer adds
           that class INSIDE its own beforeprint handler, and the shell's listener is
           registered first (it mounts before any drawer), so the class is reliably absent
           at the moment the shell would look. This attribute exists from the moment the
           drawer renders, which makes the check order-independent. */
        data-printable={printable ? '' : undefined}
        style={size ? undefined : { width }}
        role="dialog"
        aria-modal="true"
        aria-label={!title ? ariaLabel : undefined}
        aria-labelledby={title ? 'p-drawer-title' : undefined}
        tabIndex={-1}
        onKeyDown={onKeyDown}
      >
        {/* BARE header: a drawer given neither title nor subtitle still rendered the full
            header bar — 16px/20px padding, a 32px close button and a bottom border — around
            an empty flex div. On the two-pane deal record (which draws its own heading inside
            the body) that measured 65px of blank chrome, pushing BOTH columns 85px down the
            panel. `--bare` lifts the strip out of the flex column and floats just the close
            button over the top-right, so the body starts at the panel edge. A drawer WITH a
            title is untouched. */}
        <div className={`p-drawer-header${!title && !subtitle ? ' p-drawer-header--bare' : ''}`}>
          <div style={{ flex: 1, minWidth: 0 }}>
            {title && (
              <div id="p-drawer-title" className="p-drawer-title">
                {title}
              </div>
            )}
            {subtitle && <div className="p-drawer-subtitle">{subtitle}</div>}
          </div>
          <button
            type="button"
            className="p-drawer-close p-focusable"
            onClick={onClose}
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className={`p-drawer-body${bodyClassName ? ` ${bodyClassName}` : ''}`}>{children}</div>

        {footer && <div className="p-drawer-footer">{footer}</div>}
      </div>
    </>,
    document.body
  );
}
